import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { DONATION_AMOUNTS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMPACT = [
  "Sponsorship opportunities for qualifying athletes",
  "Tutoring and academic advising for student-athletes",
  "Mentorship, leadership, and life skills training",
  "College exposure experiences across the country",
];

export default function Donate() {
  const [selected, setSelected] = useState("donate_50");
  const [customAmount, setCustomAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const customCents = Math.round(parseFloat(customAmount) * 100);
  const isCustom = selected === "custom";
  const customValid = Number.isFinite(customCents) && customCents >= 100;
  const selectedLabel = isCustom
    ? customValid
      ? `$${parseFloat(customAmount)}`
      : ""
    : DONATION_AMOUNTS.find((a) => a.lookup_key === selected)?.label || "";

  const handleDonate = async () => {
    if (isCustom && !customValid) {
      toast.error("Enter a donation amount of at least $1.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: selected,
        amount_cents: isCustom ? customCents : undefined,
        origin_url: window.location.origin,
        donor_name: name || undefined,
        donor_email: email || undefined,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0A0A0A] border border-[#333333] text-white px-4 py-3 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#FEE123] focus:border-transparent placeholder:text-gray-500";

  return (
    <>
      <PageHeader
        eyebrow="Fuel The Flock"
        title="Donate To The Ducks"
        description="Louisiana Ducks is a non-profit organization. Every donation goes directly toward developing youth athletes on and off the field."
        testid="donate-header"
      />
      <section className="py-14 sm:py-20 bg-[#0A0A0A]" data-testid="donate-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            <Reveal>
              <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none text-white">
                Where Your Gift Goes
              </h2>
              <ul className="mt-8 space-y-4" data-testid="donate-impact-list">
                {IMPACT.map((item) => (
                  <li key={item} className="flex gap-4 items-center">
                    <span className="h-1.5 w-1.5 bg-[#FEE123] shrink-0" />
                    <span className="text-base text-gray-300 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-10 text-sm text-gray-400 leading-relaxed max-w-md" data-testid="donate-nonprofit-note">
                As a non-profit youth sports organization, Louisiana Ducks relies on the generosity of families,
                local businesses, and community supporters across the New Orleans metro area.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="bg-[#111111] border border-[#333333] p-7 sm:p-9" data-testid="donation-card">
                <h3 className="text-lg font-bold uppercase tracking-widest text-white">Choose An Amount</h3>
                <div className="mt-6 grid grid-cols-3 gap-3" data-testid="donation-amounts">
                  {DONATION_AMOUNTS.map((amt) => (
                    <button
                      key={amt.lookup_key}
                      onClick={() => setSelected(amt.lookup_key)}
                      data-testid={`donate-preset-${amt.label.replace("$", "")}`}
                      className={`py-4 text-xl font-extrabold rounded-sm border transition-all duration-200 ${
                        selected === amt.lookup_key
                          ? "bg-[#FEE123] text-black border-[#FEE123] scale-105"
                          : "bg-[#0A0A0A] text-gray-300 border-[#333333] hover:border-[#FEE123]/50 hover:text-white"
                      }`}
                    >
                      {amt.label}
                    </button>
                  ))}
                </div>

                <div className="mt-4 relative" data-testid="donate-custom-wrap">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#FEE123] text-xl font-extrabold">$</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Or enter any amount"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelected("custom");
                    }}
                    className={`${inputCls} pl-9 text-xl font-extrabold ${
                      isCustom && customAmount ? "border-[#FEE123]" : ""
                    }`}
                    data-testid="donate-custom-amount-input"
                  />
                </div>

                <div className="mt-6 space-y-4">
                  <input placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="donate-donor-name" />
                  <input type="email" placeholder="Email for receipt (optional)" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="donate-donor-email" />
                </div>

                <button
                  onClick={handleDonate}
                  disabled={loading}
                  data-testid="donate-submit-button"
                  className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#FEE123] text-black text-lg font-extrabold uppercase tracking-widest px-8 py-4 rounded-sm transition-transform duration-200 hover:scale-[1.02] hover:bg-[#FFEA61] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Redirecting To Secure Checkout..." : `Donate ${selectedLabel}`}
                  {!loading && <ArrowRight size={20} />}
                </button>

                <p className="mt-5 flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-gray-500" data-testid="donate-secure-note">
                  <ShieldCheck size={15} className="text-[#FEE123]" /> Secure card payment powered by Stripe
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
